import React from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { InputMaybe, PreferredHotelProviderType, useJoyPlaceBySourceIdQuery, useUserConsentForNearbyHotelsQuery } from '@graphql/generated';
import { PartnerHotelRedirect } from '../../components/PartnerHotelRedirect';
import { useEventInfo } from '@shared/utils/eventInfo';
import { PartnerSourceIdType } from '../../HotelBooking.types';
import { usePartnerHotelQueryParams } from '../../hooks/usePartnerHotelQueryParams';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';

const isValidPreferredHotelProviderType = (value?: InputMaybe<PreferredHotelProviderType>): value is PreferredHotelProviderType =>
  Object.values(PreferredHotelProviderType).includes(value as PreferredHotelProviderType);

export const HotelDetails = wrapWithHotelBookingRedirectProvider(({ joyPlaceId: joyPlaceIdArg }: { joyPlaceId?: string; eventHandle?: string }) => {
  useHotelBookingRedirectHealth(); // Call health tracking hook to opt out of auto `loadComplete` fallback.
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const { travelPlaceId, sourceType, source, queryParamEventId } = usePartnerHotelQueryParams(joyPlaceIdArg);

  const { value: accommodationsGuestPriceCompareType, loading: accommodationsGuestPriceCompareTypeLoading } = useRemoteFeatureFlag('accommodationsGuestPriceCompareType');
  const preferredProviderType = accommodationsGuestPriceCompareType as PreferredHotelProviderType;

  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const eventId = eventInfo?.eventId || queryParamEventId;

  const { data, loading: loadingData } = useJoyPlaceBySourceIdQuery({
    variables: {
      payload: { sourceId: travelPlaceId!, sourceType: PartnerSourceIdType[sourceType] },
      eventId: eventId,
      preferredProviderType: isValidPreferredHotelProviderType(preferredProviderType) ? preferredProviderType : undefined
    },
    batchMode: 'fast',
    skip: !travelPlaceId || !eventId || accommodationsGuestPriceCompareTypeLoading
  });

  const { loading: isTripProfitKeyQueryLoading, data: tripProfitKeyData, error: hasTripProfitKeyQueryError } = useUserConsentForNearbyHotelsQuery({
    variables: {
      consentKey: 'websiteOptOutTripProfit',
      eventId: eventId!
    },
    skip: !eventId || source !== 'tripprofit',
    batchMode: 'fast'
  });
  // websiteOptOutTripProfit is an OPT OUT value where true means disable the trip profit links and use the original link, and null | undefined | false means we can use the trip profit links feature
  const skipTripProfitRedirect = tripProfitKeyData?.userConsentKeyForEvent?.consentAnswer === true || !!hasTripProfitKeyQueryError;

  const isLoading = loadingData || loadingEventInfo || accommodationsGuestPriceCompareTypeLoading || isTripProfitKeyQueryLoading;

  const joyPlace = data?.joyPlaceBySourceId;
  const googleMaps = { gplid: joyPlace?.googlePlaceId, name: joyPlace?.name, address: joyPlace?.address, lat: joyPlace?.lat, lng: joyPlace?.long };

  useMount(() => {
    telemetry.hotelDetails.enter();
  });

  return (
    <>
      {isLoading ? (
        <JoyLogoLoader loaderKey="guest-hotel-booking-accommodations-place" />
      ) : (
        <PartnerHotelRedirect
          pageContext="HotelDetails"
          telemetryName="guestPartnerHotelDetailsRedirect"
          travelPlace={{ id: travelPlaceId, name: joyPlace?.name, url: joyPlace?.url, type: 'joyPlace' }}
          redirectMechanisms={joyPlace?.redirectMechanism}
          googleMaps={googleMaps}
          eventData={{ eventId, eventDate }}
          forceRedirectToHotelWebsite={skipTripProfitRedirect}
        />
      )}
    </>
  );
});
